/**
 * IssuedInvoices — outbound invoices issued to clients (GET /v1/finance/issued-invoices).
 * Two modes: the list, and the New Invoice form (InvoiceForm). A detail drawer shows
 * one invoice with its line items + server-computed totals and status actions (issue a
 * draft, mark sent/paid, void). Distinct from the AP "Invoices" page (inbound).
 */
import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, Plus, Download } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { fetchIssuedInvoices, setIssuedInvoiceStatus, downloadIssuedInvoicePdf } from '../../api'
import type { IssuedInvoice } from '../../types'
import { fxProvenance, money } from '../../../../lib/money'
import InvoiceForm from './InvoiceForm'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { statusTicket, neutral, ink, overlay } from '../../../../theme/tokens'

const STATUSES = ['all', 'draft', 'issued', 'sent', 'paid', 'void'] as const
const STATUS_LABEL: Record<string, string> = { all: 'All', draft: 'Draft', issued: 'Issued', sent: 'Sent', paid: 'Paid', void: 'Void' }
const INVOICE_STATUS: Record<string, { dot: string; label: string }> = {
  draft:  statusTicket.open,
  issued: statusTicket.in_progress,
  sent:   statusTicket.in_progress,
  paid:   statusTicket.resolved,
  void:   statusTicket.closed,
}

function fmtDate(d?: string): string { return d ? new Date(d).toLocaleDateString('en-GB') : '—' }
function pct(rate: number): string { return `${((rate ?? 0) * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%` }

function InvoiceStatus({ status }: { status: string }) {
  const cfg = INVOICE_STATUS[status] ?? { dot: neutral.dot, label: ink.secondary }
  return (
    <span className="inline-flex items-center gap-2 text-12 font-medium whitespace-nowrap" style={{ color: cfg.label }}>
      <span className="rounded-full flex-shrink-0" style={{ width: 6, height: 6, background: cfg.dot }} />
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

function actionsFor(status: string): { label: string; next: string; primary?: boolean }[] {
  switch (status) {
    case 'draft':  return [{ label: 'Issue', next: 'issued', primary: true }, { label: 'Void', next: 'void' }]
    case 'issued': return [{ label: 'Mark sent', next: 'sent', primary: true }, { label: 'Mark paid', next: 'paid' }, { label: 'Void', next: 'void' }]
    case 'sent':   return [{ label: 'Mark paid', next: 'paid', primary: true }, { label: 'Void', next: 'void' }]
    default:       return []
  }
}

function InvoiceDrawer({ invoice, onClose, onStatus, busy }: {
  invoice: IssuedInvoice; onClose: () => void; onStatus: (id: string, next: string) => void; busy: boolean
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose])

  const [pdfPending, setPdfPending] = useState(false)

  const handleDownloadPdf = useCallback(async () => {
    if (pdfPending) return
    setPdfPending(true)
    try {
      await downloadIssuedInvoicePdf(invoice.id, invoice.invoiceNumber)
    } catch {
      toast.error('Could not download the invoice PDF')
    } finally {
      setPdfPending(false)
    }
  }, [pdfPending, invoice.id, invoice.invoiceNumber])

  const items = invoice.lineItems ?? []
  const actions = actionsFor(invoice.status)

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(720px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="k-eyebrow uppercase">{invoice.invoiceNumber}</span>
              <InvoiceStatus status={invoice.status} />
            </div>
            <div className="text-detail-title font-semibold text-ink-primary truncate">{invoice.clientName} — {money(invoice.total ?? 0, invoice.currency)}</div>
            {/* FX provenance (SB-6): individual figures only — the captured
                base and its fixing date; GBP rows render nothing. */}
            {fxProvenance(invoice.baseAmountPence, invoice.fxRateDate) !== '' && (
              <div className="text-11 text-ink-faint mt-0.5">{fxProvenance(invoice.baseAmountPence, invoice.fxRateDate)}</div>
            )}
            <div className="text-12 text-ink-muted mt-1">Issued {fmtDate(invoice.issueDate)} · Due {fmtDate(invoice.dueDate)}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          {(invoice.clientEmail || invoice.clientAddress) && (
            <div className="rounded-tile bg-surface-panel border border-edge-panel px-5 py-4 grid grid-cols-2 gap-4">
              {invoice.clientEmail && <div><div className="k-eyebrow uppercase mb-1">Email</div><div className="text-13 text-ink-primary break-words">{invoice.clientEmail}</div></div>}
              {invoice.clientAddress && <div><div className="k-eyebrow uppercase mb-1">Address</div><div className="text-13 text-ink-primary whitespace-pre-line">{invoice.clientAddress}</div></div>}
            </div>
          )}

          {/* Nested block inside a drawer = tile radius (slice-2 ruling), not
              a full 18px card-in-card. */}
          <div className="rounded-tile border border-edge-panel overflow-hidden">
            <div className="finance-thead flex items-center gap-3 px-4 py-2 border-b border-edge-row">
              <span className="flex-1">Description</span>
              <span className="w-12 text-right">Qty</span>
              <span className="w-24 text-right">Unit</span>
              <span className="w-16 text-right">VAT</span>
              <span className="w-24 text-right">Total</span>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-3 text-12 text-ink-muted">No line items.</div>
            ) : items.map((li, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2 border-b border-edge-row-faint">
                <span className="flex-1 min-w-0 text-13 font-medium text-ink-body">{li.description || '—'}</span>
                <span className="w-12 text-right text-12 text-ink-muted tabular-nums">{li.quantity}</span>
                <span className="w-24 text-right text-12 text-ink-muted tabular-nums">{money(li.unit_price, invoice.currency)}</span>
                <span className="w-16 text-right text-12 text-ink-muted tabular-nums">{pct(li.tax_rate)}</span>
                <span className="w-24 text-right text-13 font-medium text-ink-primary tabular-nums">{money(li.total ?? (li.quantity * li.unit_price), invoice.currency)}</span>
              </div>
            ))}
            <div className="flex flex-col gap-1 items-end px-4 py-3 bg-surface-panel">
              <div className="flex gap-8 text-12"><span className="text-ink-muted">Subtotal</span><span className="w-24 text-right tabular-nums text-ink-primary">{money(invoice.subtotal, invoice.currency)}</span></div>
              <div className="flex gap-8 text-12"><span className="text-ink-muted">VAT</span><span className="w-24 text-right tabular-nums text-ink-primary">{money(invoice.taxTotal, invoice.currency)}</span></div>
              <div className="flex gap-8 text-14 font-semibold"><span className="text-ink-primary">Total</span><span className="w-24 text-right tabular-nums text-ink-primary">{money(invoice.total, invoice.currency)}</span></div>
            </div>
          </div>

          {invoice.notes && (
            <div>
              <div className="k-eyebrow uppercase mb-1">Notes</div>
              <p className="text-13 text-ink-body whitespace-pre-line">{invoice.notes}</p>
            </div>
          )}
        </div>

        {(
          <div className="px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0 flex items-center gap-2">
            {actions.map(a => (
              <button key={a.next} className={a.primary ? 'k-btn k-btn--lg' : 'k-btn-secondary'} onClick={() => onStatus(invoice.id, a.next)}>
                {busy ? '…' : a.label}
              </button>
            ))}
            <div className="ml-auto">
              <button className="k-btn-secondary k-btn-secondary--flex" onClick={handleDownloadPdf}>
                <Download size={14} />
                {pdfPending ? 'Preparing…' : 'Download PDF'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export default function IssuedInvoices() {
  const [mode, setMode] = useState<'list' | 'create'>('list')
  const [invoices, setInvoices] = useState<IssuedInvoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<(typeof STATUSES)[number]>('all')
  const [detail, setDetail] = useState<IssuedInvoice | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try { setInvoices(await fetchIssuedInvoices()); setError(null) }
    catch (e) { setError(apiErrorMessage(e, 'Failed to load invoices')) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { if (mode === 'list') load() }, [mode, load])

  if (mode === 'create') {
    return <InvoiceForm onSaved={() => setMode('list')} onCancel={() => setMode('list')} />
  }
  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={load} />

  const total = invoices.length
  const countFor = (s: string) => (s === 'all' ? total : invoices.filter(i => i.status === s).length)
  const display = invoices.filter(i => statusFilter === 'all' || i.status === statusFilter)

  async function changeStatus(id: string, next: string) {
    setBusy(true)
    try {
      const updated = await setIssuedInvoiceStatus(id, next)
      setInvoices(prev => prev.map(i => (i.id === id ? updated : i)))
      setDetail(d => (d && d.id === id ? updated : d))
      toast.success(`Invoice marked ${next}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Action failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1040, minWidth: 920 }}>
      <SectionTitle
        title="Issued Invoices"
        subtitle={`Finance · ${total} ${total === 1 ? 'invoice' : 'invoices'}`}
        action={<button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setMode('create')}><Plus size={14} /> New invoice</button>}
      />

      <div className="flex items-center gap-2 flex-wrap">
        {STATUSES.map(s => {
          const active = statusFilter === s
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors ${active ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control'}`}>
              {STATUS_LABEL[s]} <span className={active ? 'opacity-70' : 'text-ink-faint'}>{countFor(s)}</span>
            </button>
          )
        })}
      </div>

      <div className="k-card">
        {display.length === 0 ? (
          <EmptyState
            message={statusFilter === 'all' ? 'No invoices yet' : 'None in this status'}
            hint={statusFilter === 'all' ? 'Create your first invoice with the New invoice button.' : undefined}
          />
        ) : (
          <div>
            <div className="finance-thead flex items-center gap-4 px-5 py-2 border-b border-edge-row">
              <span className="w-32 flex-shrink-0">Number</span>
              <span className="flex-1 min-w-0">Client</span>
              <span className="w-24 flex-shrink-0 text-right">Issued</span>
              <span className="w-24 flex-shrink-0 text-right">Due</span>
              <span className="w-28 flex-shrink-0 text-right">Total</span>
              <span className="w-[110px] flex-shrink-0">Status</span>
            </div>
            {display.map(inv => (
              <div key={inv.id} onClick={() => setDetail(inv)}
                className="flex items-center gap-4 px-5 py-3 border-b border-edge-row-faint last:border-b-0 cursor-pointer hover:bg-surface-row-hover transition-colors">
                <span className="w-32 flex-shrink-0 text-12 font-medium text-ink-secondary tabular-nums truncate">{inv.invoiceNumber}</span>
                <span className="flex-1 min-w-0 text-14 font-medium text-ink-primary truncate">{inv.clientName}</span>
                <span className="w-24 flex-shrink-0 text-right text-12 text-ink-secondary tabular-nums">{fmtDate(inv.issueDate)}</span>
                <span className="w-24 flex-shrink-0 text-right text-12 text-ink-secondary tabular-nums">{fmtDate(inv.dueDate)}</span>
                <span className="w-28 flex-shrink-0 text-right text-14 font-medium text-ink-primary tabular-nums">{money(inv.total ?? 0, inv.currency)}</span>
                <span className="w-[110px] flex-shrink-0"><InvoiceStatus status={inv.status} /></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {detail && <InvoiceDrawer invoice={detail} onClose={() => setDetail(null)} onStatus={changeStatus} busy={busy} />}
    </div>
  )
}
